//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from '../common/service';
import PondUsageModel from './pondUse.model';

/**
 * Service for managing pond usages data (Master Data).
 *
 * Provides methods to fetch and manage pond usages-related data.
 * Inherits CRUD operations and HTTP utilities from the generic {@link Service} class.
 *
 * @module PondUsagesService
 */
@Injectable({
  providedIn: 'root'
})
export class PondUsagesService extends Service<PondUsageModel> {

  /**
   * Stores ongoing HTTP request subscriptions.
   * Used to cancel active requests and prevent memory leaks.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * Constructs an instance of PondUsagesService.
   *
   * @param {HttpClient} httpClient - The HttpClient service for making HTTP requests.
   */
  constructor(httpClient: HttpClient) {
    super(httpClient, '/aqu/ponduse/');
  }

  /**
   * Fetches the complete list of pond usages (Master Data).
   *
   * @method
   * @returns {Promise<Array<PondUsageModel>>} A Promise resolving with an array of PondUsageModel objects.
   * @throws {HttpErrorResponse} Throws an HTTP error if the request fails.
   *
   * Description:
   * This method is typically called in {@link PondUsagesComponent} to retrieve the full pond usages list.
   */
  public getAllPondUsagesList(): Promise<Array<PondUsageModel>> {
    return new Promise<Array<PondUsageModel>>((resolve, reject) => {
      const request = this.httpClient
        .get<Array<PondUsageModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=1000&order=new`, this.httpOptions)
        .subscribe(
          response => resolve(response),
          (error: HttpErrorResponse) => reject(error)
        );
      this.ongoingRequests.push(request);
    });
  }

  /**
   * Cancels all ongoing HTTP requests by unsubscribing from their observables.
   * Ensures proper cleanup and prevents memory leaks.
   *
   * Typically called in components when they are destroyed to cancel pending requests.
   *
   * @method
   * @returns {void}
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach(request => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
